import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { ReactComponent as Animation } from '../../../assets/icons/intro/camera.svg';
import PrimaryButton from '../../../components/buttons/primarybutton/PrimaryButton';
import SecondaryButton from '../../../components/buttons/secondarybutton/SecondaryButton';
import Header from '../../../components/header/Header';
import H2 from '../../../components/text/H2';
import { tag } from '../../../services/ATInternetService';

const cameraConstraints = {
	audio: false,
	video: {
		width: {
			ideal: 1920
		},
		height: {
			ideal: 1080
		},
		facingMode: {
			exact: 'environment'
		}
	},
};

const alternativeCameraConstraints = {
	audio: false,
	video: {
		width: {
			ideal: 1920
		},
		height: {
			ideal: 1080
		},
	},
};

let hasRequestedNextSlide = false;
let hasGranted = false;

const Camera = ({ onNextSlideRequested, onPrevSlideRequested, isCurrentSlide }) => {
	const [showCameraSlide, setShowCameraSlide] = useState(true);
	const [hasSkippedSlide, setHasSkippedSlide] = useState(false);
	const [isUserMediaSupported, setIsUserMediaSupported] = useState(true);

	if (navigator.permissions && navigator.permissions.query) {
		// @ts-ignore
		navigator.permissions.query({ name: 'camera' }).then(res => {
			if (res.state === 'granted') {
				setShowCameraSlide(false);
			}
		});
	}

	const requestCameraPermission = useCallback(() => {
		const video = document.getElementById('webcam__video');
		const onSuccess = (stream) => {
			// @ts-ignore
			video.srcObject = stream;
			// @ts-ignore
			video.play();
			// @ts-ignore
			video.controls = false;

			video.addEventListener('loadeddata', () => {
				if (/(android)/i.test(navigator.userAgent)) {
					const { body } = document;

					// @ts-ignore
					const rfs = body.requestFullscreen || body.webkitRequestFullScreen || body.mozRequestFullScreen || body.msRequestFullscreen;
					rfs.call(body);
				}

				if (!hasGranted) {
					tag.sendClick('intro', {
						chapter3: 'camera',
						name: 'permission-granted',
					});
					hasGranted = true;
				}
			});

			if (!hasRequestedNextSlide) onNextSlideRequested('camera');
			hasRequestedNextSlide = true;
		};

		navigator.mediaDevices.getUserMedia(cameraConstraints).then(onSuccess).catch(() => {
			navigator.mediaDevices.getUserMedia(alternativeCameraConstraints).then(onSuccess).catch(() => {
				setIsUserMediaSupported(false);
				tag.sendClick('intro', {
					chapter3: 'camera',
					name: 'permission-denied',
				});
			});
		});
	}, [onNextSlideRequested, setIsUserMediaSupported, hasRequestedNextSlide]);

	useEffect(() => {
		if (!showCameraSlide && isCurrentSlide && !hasSkippedSlide) {
			setHasSkippedSlide(true);
			requestCameraPermission();
		}
	}, [isUserMediaSupported, showCameraSlide, hasSkippedSlide, isCurrentSlide, onNextSlideRequested, setIsUserMediaSupported, requestCameraPermission]);

	if (!showCameraSlide) return <section />;

	return (
		<>
			<section className="intro__slide--start">
				<Header className="fixed top-0" />
				<div className="overflow-y-auto overflow-x-hidden w-screen pt-12 px-4">
					<div className="bg-white bg-opacity-10 rounded-3xl m-auto max-w-4xl px-4 pt-4">
						<Animation className="intro__animation" />
						<H2 className="py-6 text-center">
							De Klokhuis Scanner gebruikt je camera.<br />Is dat oké?
						</H2>
						{isUserMediaSupported && <div className="flex flex-col w-full px-8">
							<PrimaryButton onClick={requestCameraPermission}>
								Ja
							</PrimaryButton>
							<SecondaryButton
								className="text-red-500"
								onClick={() => {
									onPrevSlideRequested('camera');
									tag.sendClick('intro', {
										chapter3: 'camera',
										name: 'permission-denied',
									});
									hasRequestedNextSlide = false;
									hasGranted = false;
								}}>
								Liever niet
							</SecondaryButton>
						</div>}
						{!isUserMediaSupported && <p className="py-6 text-red-500">
							Helaas is toegang tot je camera geblokkeerd door je browserinstelingen.
						</p>}
					</div>
				</div>
			</section>
		</>
	);
}

Camera.propTypes = {
	onNextSlideRequested: PropTypes.func.isRequired,
	onPrevSlideRequested: PropTypes.func.isRequired,
	isCurrentSlide: PropTypes.bool.isRequired
}

export default Camera
